import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  Modal,
  TabItem,
  Tabs,
  useToasts,
  useModals,
  ModalProps,
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Table,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import { getTextPreview, getTextPreviewFormatted } from "@/helpers/utils.helper.ts";
import { useTemplatePreview, useTemplates } from "@/queries/use-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiTemplate, ApiTemplateType } from "@/types/template.types.ts";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export interface TemplateChooseModalProps extends ModalProps {
  candidateId: number;
}

enum TemplateChooseMode {
  chooseTemplate = "chooseTemplate",
  previewTemplate = "previewTemplate",
}

export function TemplateMessageChooseModal({ candidateId, ...props }: TemplateChooseModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const { showInfo } = useToasts();

  const { data: templates, isLoading } = useTemplates();
  const [searchTerm, setSearchTerm] = useState("");

  const filterTemplates = (templateList: ApiTemplate[] | undefined) => {
    if (!templateList || !searchTerm.trim()) return templateList;

    const term = searchTerm.toLowerCase();
    return templateList.filter(
      (template) =>
        template.title.toLowerCase().includes(term) ||
        template.subject?.toLowerCase().includes(term) ||
        template.body.toLowerCase().includes(term)
    );
  };

  const recentTemplates = filterTemplates(templates);
  const publicEmailTemplates = filterTemplates(
    templates?.filter(
      (template) => !template.is_private && template.template_type == ApiTemplateType.messenger
    )
  );
  const privateEmailTemplates = filterTemplates(
    templates?.filter(
      (template) => template.is_private && template.template_type == ApiTemplateType.messenger
    )
  );

  const [chosenTemplate, setChosenTemplate] = useState<ApiTemplate | undefined>(undefined);
  const { data: templatePreview, isFetching } = useTemplatePreview(chosenTemplate?.id, candidateId);

  const templatePreviewBody = useMemo(() => {
    if (templatePreview) {
      return getTextPreviewFormatted(templatePreview.body);
    } else {
      return "";
    }
  }, [templatePreview]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.templates.viewedChooseTemplate, {
      [analyticProperties.source]: "Messenger",
    });
  });

  const handleView = useCallback(
    (emailTemplate: ApiTemplate) => {
      setChosenTemplate(emailTemplate);
    },
    [candidateId]
  );

  const currentMode = templatePreview
    ? TemplateChooseMode.previewTemplate
    : TemplateChooseMode.chooseTemplate;

  const columnHelper = createColumnHelper<ApiTemplate>();
  const columns = useMemo<ColumnDef<ApiTemplate>[]>(() => {
    const inner: DisplayColumnDef<ApiTemplate, unknown>[] = [
      {
        header: t("common:title"),
        cell: (row) => (
          <ListItem
            icon={
              <Avatar
                icon={
                  <FontAwesomeIcon
                    icon={`fa-light ${row.row.original.template_type == ApiTemplateType.email ? "fa-envelope" : "fa-paper-plane"}`}
                  />
                }
              />
            }
            title={row.row.original.title}
            caption={getTextPreview(row.row.original.body, 200)}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        ...ColumnSizes.employee,
      },
    ];
    inner.push(
      columnHelper.display({
        id: "actions",
        cell: (cell) => (
          <Stack justify="end" gap="sm" direction="horizontal">
            <Button
              onClick={() => {
                handleView(cell.row.original);
              }}
              variant="secondary"
              isLoading={isFetching && cell.row.original.id == chosenTemplate?.id}
            >
              {t("core:preview")}
            </Button>
          </Stack>
        ),
        ...ColumnSizes.actions,
      })
    );
    return inner;
  }, [templates]);

  const goBackAction = () => {
    if (currentMode == TemplateChooseMode.chooseTemplate) {
      return undefined;
    } else {
      return () => {
        setChosenTemplate(undefined);
      };
    }
  };

  const copyToClipboard = useCallback(() => {
    if (templatePreview) {
      copyTextWithNotify(templatePreviewBody).then((copied) => {
        if (!copied) return;
        analyticsService.trackEvent(analyticEvents.templates.copied, {
          [analyticProperties.id]: chosenTemplate?.id,
        });
        showInfo({ title: t("common:copied") });
        close(id);
      });
    }
  }, [templatePreview, id, chosenTemplate]);

  return (
    <Modal
      {...props}
      size={"lg"}
      title={
        currentMode == TemplateChooseMode.chooseTemplate
          ? t("common:choose_template")
          : t("core:preview")
      }
      onBack={goBackAction()}
      withCloser={true}
      closeByEscEnabled={true}
      actions={
        currentMode == TemplateChooseMode.previewTemplate ? (
          <Stack>
            <Button onClick={copyToClipboard} size={"lg"}>
              {t("core:to_copy")}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <Box>
        {currentMode == TemplateChooseMode.chooseTemplate && (
          <Tabs>
            <TabItem title={t("common:recent")}>
              <Search
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                clearable={true}
                onClear={() => setSearchTerm("")}
                autoFocus={true}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
              />
              <Table
                stickyLastColumn={true}
                data={recentTemplates || []}
                columns={columns}
                showSkeleton={isLoading}
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
            </TabItem>
            <TabItem title={t("core:email_template_my")}>
              <Search
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                clearable={true}
                onClear={() => setSearchTerm("")}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
              />
              <Table
                stickyLastColumn={true}
                data={privateEmailTemplates || []}
                columns={columns}
                showSkeleton={isLoading}
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
            </TabItem>
            <TabItem title={t("core:email_template_shared")}>
              <Search
                onInput={(e) => setSearchTerm(e.currentTarget.value)}
                clearable={true}
                onClear={() => setSearchTerm("")}
                type={"text"}
                value={searchTerm}
                placeholder={t("common:search")}
              />
              <Table
                stickyLastColumn={true}
                data={publicEmailTemplates || []}
                columns={columns}
                showSkeleton={isLoading}
                notFoundComponent={<EmptyStateNothingFound />}
                emptyStateComponent={<EmptyState />}
              />
            </TabItem>
          </Tabs>
        )}
        {currentMode == TemplateChooseMode.previewTemplate && templatePreview && (
          <Box className={"flex flex-col p-4 whitespace-pre-wrap border border-dark/5 rounded-xs "}>
            {templatePreviewBody}
          </Box>
        )}
      </Box>
    </Modal>
  );
}
